<i18n>
{
  "en": {
    "title": "Strategic Vision for Sustainability 2017-2018",
    "description": "Everyone has a role to play in co-creating a cleaner environment and a more sustainable future. Learn about the Schneider Electric sustainability goals and targets as we prioritize green energy, renewable resources, and sustainable development.",
    "lookingAhead": {
      "section": "Looking Ahead",
      "title": "Looking Ahead",
      "overview": {
        "quote": {
          "para": "We know that we are the solution. We're able to reduce energy consumption, and to be a good partner to all our clients in helping them reduce their own carbon emissions.",
          "title": "Chief Sustainability Officer, <br>Schneider Electric",
          "company": "Schneider Electric"
        },
        "txt1": {
          "p1": "We believe that everyone has a role to play in creating a more sustainable future. The rigorous sustainability goals we set for ourselves measure our progress, but we can’t do it alone. That’s why we’ve aligned our sustainable efforts with the United Nations 17 Sustainable Development Goals. In doing so, we now join a global network of organizations and thought leaders, collectively advancing a powerful set of standards to create positive change."
        },
        "statement1": {
          "p1": "The Sustainable Development Goals are a universal call to action, existing to end poverty, protect the planet, and ensure that all people enjoy peace and prosperity by 2030."
        },
        "txt2": {
          "p1": "In the coming year, we’ll also continue to join companies and countries all over the world in taking proactive steps toward limiting climate change. We recently shared our commitments to sustainable development at COP23, and collaborated with sustainability leaders at One Planet Summit to limit global warming and its impact. We’re working toward aggressive goals, including carbon neutrality by the year 2030, with measurable reductions in our CO<sub>2</sub> emissions along the way. This year brings with it a higher set of sustainability standards for our entire ecosystem of suppliers, vendors, and the broader Schneider Electric community.",
          "p2": "A key measurement for progress is the ",
          "link": "2018 - 2020 Schneider Sustainability Impact",
          "p3": ", holding us more accountable than ever. In fact, one-third of our sustainability targets concern health-related topics."
        },
        "txt3": {
          "title": "Short- and long-term sustainability goals",
          "subtitle1": "1",
          "p1": "By 2020, reach the 21 commitments of the 2018 – 2020 Schneider Sustainability Impact statement, define a precise trajectory respecting the 2 °C scenarios up to 2050, validate it via the Science Based Targets initiative to which the company has been a signatory since 2016, and increase the impact of the Schneider Electric internal carbon price.",
          "subtitle2": "2",
          "p2": "By 2030, achieve carbon neutrality by reducing the carbon emissions of the Group's sites as well as those of its industrial ecosystem including its suppliers and customers, quantify the CO<sub>2</sub> emissions avoided by its customers thanks to its EcoStruxure offers, aim for 100% renewable energy, 100% recycled or certified packaging, 100% recovered waste, and double its energy productivity compared to 2005.",
          "subtitle3": "3",
          "p3": "By 2050, on scopes 1 and 2, reduce by more than 50% its CO<sub>2</sub> emissions in absolute value compared to 2015, in line with the guidelines of the Science Based Targets.",
          "footnote": "<sup>1</sup> Scope 1 is also referred to as Direct Green House Gases (GHG) and are defined as emissions from sources that are owned or controlled by the organization. Scope 2 is also referred to as Energy Indirect GHG and are defined as emissions from consumption of purchased sources of energy generated upstream from the organization."
        },
        "cta1": "See our targets"
      }
    }
  },
  "fr": {
    "title": "Strategic Vision for Sustainability 2017-2018",
    "description": "Everyone has a role to play in co-creating a cleaner environment and a more sustainable future. Learn about the Schneider Electric sustainability goals and targets as we prioritize green energy, renewable resources, and sustainable development.",
    "lookingAhead": {
      "section": "Anticiper demain",
      "title": "Anticiper demain",
      "overview": {
        "quote": {
          "para": "Nous savons que nous sommes la solution. La réduction de la consommation d'énergie est à notre portée, et nous accompagnons au mieux nos clients pour les aider à diminuer leurs émissions de CO<sub>2</sub>.",
          "title": "Directeur Développement durable, <br>Schneider Electric",
          "company": "Schneider Electric"
        },
        "txt1": {
          "p1": "Nous sommes convaincus que chacun a un rôle à jouer dans la création d'un avenir plus durable. Nous mesurons les progrès accomplis à l’aune des engagements en matière de développement durable ambitieux que nous nous sommes fixés, mais seuls, nous ne pouvons y arriver. C’est pourquoi nous avons aligné nos efforts en matière de développement durable sur les 17 Objectifs de Développement Durable lancés par les Nations unies. Ce faisant, nous avons rejoint un réseau mondial d'organisations et de leaders d’opinion qui unissent leurs forces pour proposer un éventail de normes efficaces dans le but d’initier un changement positif."
        },
        "statement1": {
          "p1": "Les 17 Objectifs de Développement Durable constituent un appel universel à l’action pour éradiquer la pauvreté, protéger la planète et faire en sorte que tous les êtres humains vivent dans la paix et la prospérité d’ici 2030."
        },
        "txt2": {
          "p1": "Cette année, nous continuerons de nous associer à des entreprises et des pays qui, partout dans le monde, adoptent des mesures proactives pour limiter le changement climatique. Nous avons récemment exposé nos engagements en faveur du développement durable à l’occasion de la COP23, et nous avons collaboré avec les chefs de file du mouvement lors du One Planet Summit visant à limiter le réchauffement climatique et son incidence. Nous œuvrons en faveur d’objectifs ambitieux, à commencer par la neutralité carbone à l’horizon 2030, et une réduction quantifiable de nos émissions de CO<sub>2</sub> d'ici là.  Cette année sera marquée par la mise en place Objectifs de Développement Durable plus ambitieux pour tout notre écosystème de fournisseurs et de prestataires, ainsi que pour les parties prenantes de Schneider Electric au sens large.",
          "p2": "Nous avons également lancé notre nouveau baromètre ",
          "link": "2018 - 2020 Schneider Sustainability Impact",
          "p3": ". Il accroît notre ambition en matière de développement durable et nous permet, comme pour les précédentes éditions, de rendre compte de nos progrès chaque trimestre, chiffres à l’appui.  Cette cinquième édition comprend 21 indicateurs, contre 16 dans les versions précédentes, pour refléter notre fort degré d’engagement en faveur d'un changement positif.",
          "extra_para": [
            "En tant qu’entreprise, nous sommes donc bien placés pour relever les défis majeurs de ce siècle en matière de développement durable. La stabilité du climat, le respect des droits humains dans le monde et la lutte contre la corruption, l'égalité des chances et le bien-être sont pour nous des priorités.",
            "Nous avons également toutes les cartes en main pour assurer une cohérence entre nos différents objectifs de développement durable, grâce à un meilleur alignement de nos objectifs à 3 ans, à 10 ans et à 20-30 ans. Une manière de garantir l’homogénéité des progrès accomplis afin de tenir nos engagements : avoir recours aux énergies renouvelables, tendre vers le zéro déchet, doubler notre productivité énergétique, atteindre la neutralité carbone, etc.",
            "Le développement durable est notre raison d'être. À cet égard, nous accordons la priorité à des objectifs spécifiques et quantifiables. Car, chez Schneider Electric, développement durable et développement économique vont de pair. Nous nous engageons à quantifier le niveau d'émissions de CO<sub>2</sub> évitées par nos clients grâce à nos offres, à atteindre 100 % d'électricité d'origine renouvelable, 100 % de packaging recyclé ou certifié, 100 % de déchets valorisés, et à doubler notre productivité énergétique par rapport à 2005. Nous agissons de manière proactive pour co-créer un avenir durable."
          ]
        },
        "txt3": {
          "title": "Objectifs de développement durable du Groupe à court et long terme",
          "subtitle1": "1",
          "p1": " D’ici 2020, atteindre  les 21 nouveaux engagements pris dans le cadre du baromètre Schneider Sustainability Impact pour la période 2018– 2020, définir une trajectoire précise dans le respect des scénarios de 2 °C à l'échéance 2050, la  valider via l’initiative <em>Science-based targets</em> dont le Groupe est signataire depuis 2016 et accroître l’impact de son prix interne du carbone.",
          "subtitle2": "2",
          "p2": "D’ici 2030, atteindre la neutralité carbone en réduisant les émissions de CO<sub>2</sub> sur nos sites ainsi que celles de notre écosystème industriel englobant nos fournisseurs et clients, quantifier les émissions de CO<sub>2</sub> évitées par nos clients grâce à nos offres EcoStruxure, atteindre 100 % d'électricité renouvelable, 100 % de packaging recyclé ou certifié, 100 % de déchets valorisés, et doubler notre  productivité énergétique par rapport à 2005.",
          "subtitle3": "3",
          "p3": "D’ici 2050, sur les scopes 1 et 2, réduire de plus de 50 % nos émissions de CO<sub>2</sub> en valeur absolue par rapport à 2015, conformément aux lignes directrices des Science-based targets.",
          "footnote": "Le scope 1 concerne les émissions directes de GES provenant des installations fixes ou  mobiles situées à l'intérieur du périmètre organisationnel. Le scope 2 concerne les émissions indirectes associées à la production d'électricité, de chaleur ou de vapeur, importée pour les activités de l'organisation."
        },
        "cta1": "Découvrir nos objectifs"
      }
    }
  }
}
</i18n>
<template>
  <div id="">
    <Hero :loResSrc="require('@/assets/img/homepage/looking-forward-low.jpg')" :hiResSrc="require('@/assets/img/homepage/looking-forward.jpg')" chapter="07" :title="$t('lookingAhead.title')" :grayBG="true" />

    <section class="m-b-120">
      <Quote :photo="require('@/assets/img/looking-ahead/Gilles_Vermot_Desroches_2017.jpg')" name="Gilles Vermot Desroches" :title="$t('lookingAhead.overview.quote.title')">
        <div v-html="$t('lookingAhead.overview.quote.para')"></div>
      </Quote>
    </section>

    <section class="m-b-120 m-t-120">
      <Txt class="m-b-32">
        <p v-html="$t('lookingAhead.overview.txt1.p1')"/>
      </Txt>

      <Statement class="m-b-50">
        <h3 v-html="$t('lookingAhead.overview.statement1.p1')"></h3>
      </Statement>

      <Txt class="m-b-50">
        <p v-html="$t('lookingAhead.overview.txt2.p1')"/>
        <div class="m-b-32">
          <p>
            <span v-html="$t('lookingAhead.overview.txt2.p2')"/>
            <router-link v-html="$t('lookingAhead.overview.txt2.link')" :to="{ name: 'Introduction_WorkIndex', params: { lang: $route.params.lang}}"></router-link><span v-html="$t('lookingAhead.overview.txt2.p3')" />
          </p>
          <p v-if="$i18n.locale === 'fr'" v-html="$t('lookingAhead.overview.txt2.extra_para[0]')"></p>
          <p v-if="$i18n.locale === 'fr'" v-html="$t('lookingAhead.overview.txt2.extra_para[1]')"></p>
          <p v-if="$i18n.locale === 'fr'" v-html="$t('lookingAhead.overview.txt2.extra_para[2]')"></p>
        </div>
        <router-link class="cta-button-route" :to="{ name: 'Introduction_WorkIndex'}" v-html="$t('lookingAhead.overview.cta1')"></router-link>
      </Txt>
    </section>

    <hr>

    <section class="m-t-120 m-b-120">
      <FourCols>
        <div slot="above">
          <h2 id="sustainabilitygoals" v-html="$t('lookingAhead.overview.txt3.title')"/>
        </div>
        <FourColsItem>
          <img v-if="$i18n.locale === 'en'" :src="require('@/assets/img/looking-ahead/2020-1.jpg')" alt="">
          <img v-if="$i18n.locale === 'fr'" :src="require('@/assets/img/looking-ahead/D’ici_2020.jpg')" alt="">
          <p id="2020goals" v-html="$t('lookingAhead.overview.txt3.p1')"/>
        </FourColsItem>

        <FourColsItem>
          <img v-if="$i18n.locale === 'en'" :src="require('@/assets/img/looking-ahead/2030-1.jpg')" alt="">
          <img v-if="$i18n.locale === 'fr'" :src="require('@/assets/img/looking-ahead/D’ici_2030.jpg')" alt="">
          <p id="2030goals" v-html="$t('lookingAhead.overview.txt3.p2')"/>
        </FourColsItem>

        <FourColsItem>
          <img v-if="$i18n.locale === 'en'" :src="require('@/assets/img/looking-ahead/2050-1.jpg')" alt="">
          <img v-if="$i18n.locale === 'fr'" :src="require('@/assets/img/looking-ahead/D’ici_2050.jpg')" alt="">
          <p id="2050goals" v-html="$t('lookingAhead.overview.txt3.p3')"/>
          <p class="t-footnote" v-html="$t('lookingAhead.overview.txt3.footnote')"></p>
        </FourColsItem>
      </FourCols>
    </section>
   <div class="l-content">
      <FooterNav
        nextRoute="LookingAhead_2018Priorities"
        prevRoute="CompanyPerformance_Ethics" />
    </div>

  </div>
</template>

<script>
import FooterNav from '@/components/FooterNav'
import Hero from '@/components/Hero'
import Quote from '@/components/Quote'
import Statement from '@/components/Statement'
import FourCols from '@/components/FourCols'
import FourColsItem from '@/components/FourColsItem'
import CTA from '@/components/CTA'
import Txt from '@/components/Txt'
import References from '@/components/References'
import Reference from '@/components/Reference'
import YouTubeVideo from '@/components/YouTubeVideo'
import ImageFull from '@/components/ImageFull'

export default {
  name: 'LookingAhead_Overview',
  metaInfo () {
    return {
      title: this.$t('title'),
      meta: [
        { name: 'description', content: this.$t('description') }
      ]
    }
  },
  mounted () {
    if (window.location.hash.substr(1).length > 0) {
      this.$nextTick(function () {
        document.getElementById(window.location.hash.substr(1)).scrollIntoView()
      })
    }
  },
  components: {
    FooterNav,
    Hero,
    Quote,
    Statement,
    FourCols,
    FourColsItem,
    CTA,
    Txt,
    References,
    Reference,
    YouTubeVideo,
    ImageFull
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/looking-ahead/overview.vue